import { useState } from 'react';
import {
  Plus, Copy, CheckCircle2, UserPlus, Pencil, Ban, Trash2, RotateCcw, X,
} from 'lucide-react';
import {
  useAdminTenants,
  useAdminUsers,
  useCreateAdminUser,
  useCurrentUser,
  useDeleteAdminUser,
  useUpdateAdminUser,
  type AdminUser,
} from '../../lib/api/queries';
import {
  Modal, ModalHeader, ModalActions, IconButton, Field, Th, Td, inputCls,
} from './components';

/**
 * Slice 8 super-user surface. Lists every user across every tenant and
 * lets a platform admin create / edit / disable / delete users.
 *
 * Hosted under AdminShell (which gates access on isPlatformAdmin) so this
 * component can focus on the table + actions. Server-side, /api/admin/* is
 * the actual security boundary.
 */
export function AdminUsersPage() {
  const [creating, setCreating] = useState(false);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Users</h1>
          <p className="text-sm text-gray-500">
            Every user across every tenant. Creating a user issues a one-time temporary
            password and emails the new user; the password is also surfaced once in this UI
            as a fallback.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium
                     text-white bg-accent-alt-600 hover:bg-accent-alt-700 rounded-md
                     transition-colors"
        >
          <Plus className="w-4 h-4" aria-hidden />
          New user
        </button>
      </div>

      <UserTable />

      {creating && <CreateUserModal onClose={() => setCreating(false)} />}
    </div>
  );
}

function UserTable() {
  const { data: users, isLoading, error } = useAdminUsers();
  const { data: me } = useCurrentUser();
  const [editing,   setEditing]   = useState<AdminUser | null>(null);
  const [confirming, setConfirming] = useState<
    { user: AdminUser; action: 'disable' | 'enable' | 'delete' } | null
  >(null);
  const update = useUpdateAdminUser();
  const remove = useDeleteAdminUser();

  if (isLoading)        return <p className="text-sm text-gray-500">Loading users…</p>;
  if (error)            return <p className="text-sm text-danger-500">Couldn't load users.</p>;
  if (!users?.length)   return <p className="text-sm text-gray-500">No users yet.</p>;

  const runConfirm = async () => {
    if (!confirming) return;
    if (confirming.action === 'delete') {
      await remove.mutateAsync(confirming.user.userId);
    } else {
      await update.mutateAsync({
        id: confirming.user.userId,
        isDisabled: confirming.action === 'disable',
      });
    }
    setConfirming(null);
  };

  return (
    <>
      <section className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <Th>Email</Th>
              <Th>Name</Th>
              <Th>Tenant</Th>
              <Th>Roles</Th>
              <Th>MFA</Th>
              <Th>Status</Th>
              <th className="px-4 py-2 text-right font-medium w-36">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {users.map(u => {
              const isSelf = me?.userId === u.userId;
              return (
                <tr key={u.userId} className={u.isDisabled ? 'bg-gray-50/60' : 'hover:bg-gray-50'}>
                  <Td>
                    <span className={'font-medium ' + (u.isDisabled ? 'text-gray-500' : 'text-gray-900')}>
                      {u.email}
                    </span>
                  </Td>
                  <Td>{u.displayName}</Td>
                  <Td><span className="text-gray-700">{u.tenantName}</span></Td>
                  <Td>
                    {u.isPlatformAdmin ? (
                      <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5
                                       rounded-full bg-accent-alt-50 text-accent-alt-700
                                       border border-accent-alt-500/30">
                        Platform admin
                      </span>
                    ) : (
                      <span className="text-xs text-gray-500">User</span>
                    )}
                  </Td>
                  <Td>
                    {u.mfaEnabled ? (
                      <span className="inline-flex items-center gap-1 text-xs text-success-600">
                        <CheckCircle2 className="w-3.5 h-3.5" aria-hidden /> Enabled
                      </span>
                    ) : (
                      <span className="text-xs text-gray-500">Not yet</span>
                    )}
                  </Td>
                  <Td>
                    {u.isDisabled ? (
                      <span className="text-xs text-danger-500">Disabled</span>
                    ) : (
                      <span className="text-xs text-success-600">Active</span>
                    )}
                  </Td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="inline-flex items-center gap-1">
                      <IconButton title="Edit" onClick={() => setEditing(u)}>
                        <Pencil className="w-4 h-4" aria-hidden />
                      </IconButton>
                      {u.isDisabled ? (
                        <IconButton title="Enable"
                                    onClick={() => setConfirming({ user: u, action: 'enable' })}>
                          <RotateCcw className="w-4 h-4" aria-hidden />
                        </IconButton>
                      ) : (
                        <IconButton title={isSelf ? "You can't disable yourself" : 'Disable'}
                                    disabled={isSelf}
                                    onClick={() => setConfirming({ user: u, action: 'disable' })}>
                          <Ban className="w-4 h-4" aria-hidden />
                        </IconButton>
                      )}
                      <IconButton title={isSelf ? "You can't delete yourself" : 'Delete'}
                                  danger
                                  disabled={isSelf}
                                  onClick={() => setConfirming({ user: u, action: 'delete' })}>
                        <Trash2 className="w-4 h-4" aria-hidden />
                      </IconButton>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      {editing && (
        <EditUserModal user={editing} onClose={() => setEditing(null)} />
      )}
      {confirming && (
        <ConfirmModal
          title={
            confirming.action === 'delete' ? 'Delete user'
              : confirming.action === 'disable' ? 'Disable user'
              : 'Enable user'
          }
          body={
            confirming.action === 'delete'
              ? <>Delete <strong>{confirming.user.email}</strong>? Their account is removed; audit history they created remains intact (assessor names are stored as text).</>
              : confirming.action === 'disable'
              ? <>Disable <strong>{confirming.user.email}</strong>? They won't be able to sign in until you enable them again.</>
              : <>Enable <strong>{confirming.user.email}</strong>? They'll be able to sign in with their existing password.</>
          }
          confirmLabel={confirming.action === 'delete' ? 'Delete' : confirming.action === 'disable' ? 'Disable' : 'Enable'}
          danger={confirming.action !== 'enable'}
          pending={remove.isPending || update.isPending}
          onCancel={() => setConfirming(null)}
          onConfirm={runConfirm}
        />
      )}
    </>
  );
}

function EditUserModal({
  user, onClose,
}: { user: AdminUser; onClose: () => void }) {
  const { data: me } = useCurrentUser();
  const tenants = useAdminTenants();
  const update  = useUpdateAdminUser();

  const isSelf = me?.userId === user.userId;
  const [firstName,        setFirstName]        = useState(user.firstName ?? '');
  const [lastName,         setLastName]         = useState(user.lastName ?? '');
  const [tenantId,         setTenantId]         = useState(user.tenantId);
  const [isPlatformAdmin,  setIsPlatformAdmin]  = useState(user.isPlatformAdmin);
  const [serverError,      setServerError]      = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setServerError(null);
    try {
      await update.mutateAsync({
        id: user.userId,
        firstName: firstName || null,
        lastName:  lastName  || null,
        tenantId,
        isPlatformAdmin,
      });
      onClose();
    } catch (err) {
      const detail = (err as { detail?: string })?.detail;
      const status = (err as { status?: number })?.status;
      setServerError(detail ?? (status ? `Request failed (${status})` : 'Request failed'));
    }
  };

  return (
    <Modal onClose={onClose}>
      <ModalHeader icon={<Pencil className="w-5 h-5 text-accent-alt-600" />} onClose={onClose}>
        Edit user
      </ModalHeader>
      <p className="text-xs text-gray-500 mb-3">{user.email}</p>
      <form onSubmit={onSubmit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="First name">
            <input type="text" value={firstName}
                   onChange={e => setFirstName(e.target.value)}
                   className={inputCls} />
          </Field>
          <Field label="Last name">
            <input type="text" value={lastName}
                   onChange={e => setLastName(e.target.value)}
                   className={inputCls} />
          </Field>
        </div>
        <Field label="Tenant">
          <select value={tenantId}
                  onChange={e => setTenantId(e.target.value)}
                  disabled={tenants.isLoading}
                  className={inputCls}>
            {tenants.data?.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </Field>
        <label className={'flex items-center gap-2 text-sm select-none '
                          + (isSelf && isPlatformAdmin ? 'text-gray-400' : 'text-gray-700')}>
          <input type="checkbox" checked={isPlatformAdmin}
                 disabled={isSelf && isPlatformAdmin}
                 onChange={e => setIsPlatformAdmin(e.target.checked)}
                 className="rounded border-gray-300 text-accent-alt-600 focus:ring-accent-alt-500" />
          Platform administrator
          {isSelf && isPlatformAdmin && (
            <span className="text-xs text-gray-500">(can't revoke your own)</span>
          )}
        </label>
        {serverError && <p className="text-sm text-danger-500">{serverError}</p>}
        <ModalActions
          onCancel={onClose}
          confirmLabel={update.isPending ? 'Saving…' : 'Save'}
          pending={update.isPending}
        />
      </form>
    </Modal>
  );
}

function ConfirmModal({
  title, body, confirmLabel, danger, pending, onCancel, onConfirm,
}: {
  title: string;
  body: React.ReactNode;
  confirmLabel: string;
  danger?: boolean;
  pending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Modal onClose={onCancel}>
      <ModalHeader onClose={onCancel}>{title}</ModalHeader>
      <p className="text-sm text-gray-700 mb-4">{body}</p>
      <ModalActions
        onCancel={onCancel}
        confirmLabel={pending ? 'Working…' : confirmLabel}
        pending={pending}
        danger={danger}
        onConfirm={onConfirm}
      />
    </Modal>
  );
}

function CreateUserModal({ onClose }: { onClose: () => void }) {
  const tenants = useAdminTenants();
  const create  = useCreateAdminUser();

  const [email,          setEmail]          = useState('');
  const [firstName,      setFirstName]      = useState('');
  const [lastName,       setLastName]       = useState('');
  const [tenantId,       setTenantId]       = useState('');
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [serverError,    setServerError]    = useState<string | null>(null);

  // After a successful create, swap into a "show the temp password" view —
  // the password is the only thing the super-user needs out of the response,
  // and it can't be retrieved later.
  const issued = create.data;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setServerError(null);
    try {
      await create.mutateAsync({
        email,
        firstName: firstName || null,
        lastName:  lastName  || null,
        tenantId,
        isPlatformAdmin,
      });
    } catch (err) {
      const status = (err as { status?: number })?.status;
      const detail = (err as { detail?: string })?.detail;
      setServerError(detail ?? (status ? `Request failed (${status})` : 'Request failed'));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center px-4 z-50"
         onClick={onClose}>
      <div className="w-full max-w-md bg-white rounded-lg shadow-lg p-6"
           onClick={e => e.stopPropagation()}>
        <header className="flex items-center justify-between gap-3 mb-4">
          <h2 className="text-lg font-semibold text-gray-900 inline-flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-accent-alt-600" aria-hidden />
            {issued ? 'User created' : 'Create user'}
          </h2>
          <button type="button" onClick={onClose}
                  className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" aria-hidden />
          </button>
        </header>

        {issued ? (
          <IssuedView issued={issued} onClose={onClose} />
        ) : (
          <form onSubmit={onSubmit} className="space-y-3">
            <Field label="Email">
              <input type="email" required value={email}
                     onChange={e => setEmail(e.target.value)}
                     className={inputCls} placeholder="user@tenant.test" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="First name">
                <input type="text" value={firstName}
                       onChange={e => setFirstName(e.target.value)}
                       className={inputCls} placeholder="Sammy" />
              </Field>
              <Field label="Last name">
                <input type="text" value={lastName}
                       onChange={e => setLastName(e.target.value)}
                       className={inputCls} placeholder="Wong" />
              </Field>
            </div>
            <Field label="Tenant">
              <select required value={tenantId}
                      onChange={e => setTenantId(e.target.value)}
                      className={inputCls}
                      disabled={tenants.isLoading}>
                <option value="">— select tenant —</option>
                {tenants.data?.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </Field>
            <label className="flex items-center gap-2 text-sm text-gray-700 select-none">
              <input type="checkbox" checked={isPlatformAdmin}
                     onChange={e => setIsPlatformAdmin(e.target.checked)}
                     className="rounded border-gray-300 text-accent-alt-600
                                focus:ring-accent-alt-500" />
              Make this user a platform administrator
            </label>

            {serverError && (
              <p className="text-sm text-danger-500">{serverError}</p>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={onClose}
                      className="px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100
                                 rounded-md transition-colors">
                Cancel
              </button>
              <button type="submit" disabled={create.isPending}
                      className="px-3 py-1.5 text-sm font-medium text-white
                                 bg-accent-alt-600 hover:bg-accent-alt-700 rounded-md
                                 disabled:opacity-50 transition-colors">
                {create.isPending ? 'Creating…' : 'Create user'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function IssuedView({
  issued, onClose,
}: {
  issued: { user: AdminUser; tempPassword: string };
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    await navigator.clipboard.writeText(issued.tempPassword);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-700">
        <span className="font-medium">{issued.user.email}</span> has been added to{' '}
        <span className="font-medium">{issued.user.tenantName}</span>.
        Share the temporary password with them out-of-band — this is the only time it's shown.
      </p>
      <div className="border border-gray-200 rounded-md p-3 bg-gray-50">
        <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
          Temporary password
        </div>
        <div className="flex items-center justify-between gap-3">
          <code className="text-sm font-mono text-gray-900 break-all">
            {issued.tempPassword}
          </code>
          <button type="button" onClick={copy}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium
                             text-gray-700 hover:bg-gray-100 rounded-md transition-colors">
            {copied ? (
              <>
                <CheckCircle2 className="w-3.5 h-3.5 text-success-500" aria-hidden /> Copied
              </>
            ) : (
              <>
                <Copy className="w-3.5 h-3.5" aria-hidden /> Copy
              </>
            )}
          </button>
        </div>
      </div>
      <p className="text-xs text-gray-500">
        The user must complete MFA enrollment on first sign-in. They should rotate
        the password from their account settings immediately after.
      </p>
      <div className="flex justify-end pt-1">
        <button type="button" onClick={onClose}
                className="px-3 py-1.5 text-sm font-medium text-white
                           bg-accent-alt-600 hover:bg-accent-alt-700 rounded-md
                           transition-colors">
          Done
        </button>
      </div>
    </div>
  );
}

